import { FieldError } from "./api";
import { OidcClientSettings, Profile } from "oidc-client";
import configureStore from "./store";
import { ClientSettings } from "@dvrt/web-phone-client";
import { defaultMeetingPageSettings, MeetingPageSettings } from "./components/meeting-page/types";
import { defaultRoomSettings, RoomSettings } from "./components/meeting-page/room/types";
import { RoomState } from "./components/meeting-page/room/redux";
import { DebugSettings, defaultDebugSettings } from "./components/debug/types";
import { ControlPosition } from "react-draggable";
import { AnswerOptions } from "@dvrt/jssip";
import { Position, Props } from "react-rnd";
import {
    defaultSelfVideoDialogSettings,
    SelfVideoDialogSettings,
} from "./components/meeting-page/room/self-video/tools";
import { MessageDescriptor } from "react-intl";

export type NotificationVariant = "success" | "warning" | "error" | "info";

export type NotificationMessageVars = { [key: string]: any };

export type Notification = {
    show: boolean;
    message: MessageDescriptor;
    variant: NotificationVariant;
    messageVars: NotificationMessageVars;
};

export type State = {
    notification: Notification;
    lastMeetingId: number;
    backendError: BackendError;
    theme: "light" | "dark";
    room: RoomState;
};

export type ConcertoDownloadLink = {
    android: string;
    iphone: string;
    linux: string;
    mac: string;
    windows: string;
};

declare global {
    interface Window {
        tequet: {
            sipUri: string;
            mixerDomain: string;
            proxyUrl: string;
        };
        conferenceNumbers: string[];
        concertoDownloadLink: ConcertoDownloadLink;
        oidcClientSettings: OidcClientSettings;
        existingLanguageTags: string[];
        debugEnabled: boolean;
        concertoClientJoinBaseUrl: string;
        chattoBaseUrl: string;
        supportedBrowsers: Record<string, string>;
        desktopShareConstraints: MediaStreamConstraints;
        videoConstraintsOverrides: MediaTrackConstraints;
        callOptionsOverrides: AnswerOptions;
        meetingLengthSelectOptions: number[];
        videoBackgroundEffectsEnabled: boolean;
        CanvasCaptureMediaStreamTrack: any;
    }

    interface MediaDevices {
        getDisplayMedia(constraints?: MediaStreamConstraints): Promise<MediaStream>;
    }

    interface HTMLCanvasElement {
        captureStream(frameRate?: number): MediaStream;
    }
}

export type FieldErrorList = FieldError[];

export type OmitTypeName<T extends { __typename?: any }> = Omit<T, "__typename">;

export function removeTypename<T extends { __typename?: any }>(source: T): OmitTypeName<T> {
    if (source.__typename) delete source.__typename;
    return source;
}

export type ItemRect = Position & Props["size"];

export type WindowPanelSettings = {
    position: ControlPosition;
    scale: number;
    opacity: number;
};

export class LocalStorageSchema extends ClientSettings {
    hidePastMeetingsInList: boolean = false;
    accessToken: string = null;
    refreshToken: string = null;
    profile: Profile = null;
    beforeLoginUrl: string = null;
    debugSettings: DebugSettings = defaultDebugSettings;
    windowPanelSettings: Record<string, WindowPanelSettings> = {};
    selfVideoDialogSettings: SelfVideoDialogSettings = defaultSelfVideoDialogSettings;
}

export class SessionStorageSchema {
    roomSettings: RoomSettings = defaultRoomSettings;
    meetingPageSettings: MeetingPageSettings = defaultMeetingPageSettings;
    clientIdentifier: string = null;
}

export const backendErrorCodes = [
    "not_authorized",
    "unreachable",
    "expired_access_token",
    "unknown_access_token",
    "cant_reach_heimdall",
    "unknown_invitation",
    "unknown",
] as const;

export type BackendErrorCode = (typeof backendErrorCodes)[number];

/**
 * The backend also answers with the error codes of the GraphQL library, like "INTERNAL_SERVER_ERROR", which have no
 * translation and no handling. They are reported as an unknown error.
 */
export function toBackendErrorCode(code: unknown): BackendErrorCode {
    return backendErrorCodes.includes(code as BackendErrorCode) ? (code as BackendErrorCode) : "unknown";
}

export type BackendError = {
    code: BackendErrorCode;
    details?: string;
};

export type TStore = ReturnType<typeof configureStore>;
